import { LocaleResources } from 'locales';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext } from 'react';
import { localeDomains } from 'src/config/localeDomains';
import { I18nContext } from 'src/stores/I18n';
import { HeadFavicon } from './HeadFavicon';

const buildCanonicalUrl = (locale: string, path: string, basePath: string): string => {
  const domain: string = localeDomains(locale);
  const pathWithoutSlash = path.replace(/^\/+/, '');
  const basePathWithoutSlash = basePath.replace(/^\/+/, '');
  return `${domain}/${basePathWithoutSlash}/${pathWithoutSlash}`;
};

const siteMetaLogoForLocale = (locale: string) => {
  switch (locale) {
    case 'en':
      return '/kaimono/images/ogp/ogp-en-1200x630.webp';
    case 'zh_tw':
      return '/kaimono/images/ogp/ogp-tw-1200x630.webp';
    case 'zh_hk':
      return '/kaimono/images/ogp/ogp-hk-1200x630.webp';
    default:
      return '/kaimono/images/ogp/ogp-tw-1200x630.webp';
  }
};

const languageTerritoryNotation = (locale: string) => {
  switch (locale) {
    case 'en':
      return 'en_US';
    case 'ja':
      return 'ja_JP';
    case 'zh_hk':
      return 'zh_HK';
    case 'zh_tw':
      return 'zh_TW';
    case 'zh_cn':
      return 'zh_CN';
    default:
      return 'zh_TW';
  }
};

const getEffectiveRobots = (locale: string, robotsIndex: boolean) => {
  if (locale === 'zh_cn' || process.env.NEXT_PUBLIC_WAMAZING_ENV !== 'production') {
    return 'noindex,nofollow';
  }
  return robotsIndex ? 'index' : 'noindex,nofollow';
};

const getDefaultMetaContent = (locale: string, localeResources: LocaleResources) => ({
  title: localeResources.commons.meta.title,
  desc: localeResources.staticWalkthrough.description,
  imgUrl: localeDomains(locale) + siteMetaLogoForLocale(locale),
});

type Props = {
  title?: string;
  description?: string;
  image?: string;
  robotsIndex?: boolean;
  path?: string;
};

export const HtmlHead: React.FC<Props> = (props: Props) => {
  const { locale, basePath, pathname } = useRouter();
  const currentLocale = locale || 'en';
  const localeResources = useContext(I18nContext);
  const defaultMetaContent = getDefaultMetaContent(currentLocale, localeResources);
  const {
    title = defaultMetaContent.title,
    description = defaultMetaContent.desc,
    image = defaultMetaContent.imgUrl,
    robotsIndex = false,
    path = '',
  } = props;
  const canonicalUrl = buildCanonicalUrl(currentLocale, path, basePath);
  const effectiveRobots = getEffectiveRobots(currentLocale, robotsIndex);
  const ogpTypeMap = new Map<string, string>([
    ['/', 'website'],
    ['/products/[id]', 'product'],
  ]);
  const ogType = ogpTypeMap.get(pathname) || 'article';
  const localesForHrefLang = {
    en: 'en',
    zh_tw: 'zh-Hant-TW',
    zh_hk: 'zh-Hant-HK',
    zh_cn: 'zh-Hans-CN',
  };

  return (
    <>
      <Head>
        {/* opengraph basic metadata */}
        <meta property="og:image" content={image} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content={ogType} />
        {/* opengraph optional metadata  */}
        <meta property="og:site_name" content={defaultMetaContent.title} />
        <meta property="og:locale" content={languageTerritoryNotation(currentLocale)} />
        <title>{title}</title>
        <meta name="viewport" content="initial-scale=1.0, maximum-scale=5.0, width=device-width, viewport-fit=cover" />
        {/* seo metadata */}
        <meta name="robots" content={effectiveRobots} />
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        {Object.entries(localesForHrefLang).map(([locale, hrefLang]) => (
          <link key={locale} rel="alternate" hrefLang={hrefLang} href={buildCanonicalUrl(locale, path, basePath)} />
        ))}
      </Head>
      <HeadFavicon />
    </>
  );
};
